import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { BarChart3, Calculator, Settings, ChevronDown, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  formatOdds,
  formatMarketType,
  formatGameTime,
  sportsbookIcons,
  sportsbookNames,
  getOddsFormat,
  setOddsFormat as saveOddsFormat
} from '../utils/oddsUtils';
import DataBanner from './DataBanner';
import FilterBar from './FilterBar';
import MarketBadge from './MarketBadge';

function PositiveEV() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bankroll, setBankroll] = useState(10000);
  const [kellyMultiplier, setKellyMultiplier] = useState(0.25);
  const [maxOdds, setMaxOdds] = useState(10.0);
  const [selectedLeagues, setSelectedLeagues] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [selectedKeytypes, setSelectedKeytypes] = useState([]);
  const [dateLimit, setDateLimit] = useState(null);
  const [showOnlyMyBooks, setShowOnlyMyBooks] = useState(false);
  const [showWhatIs, setShowWhatIs] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [oddsFormat, setOddsFormat] = useState(getOddsFormat());
  const { getToken } = useAuth();
  const { user } = useUser();
  const myBooks = user?.unsafeMetadata?.selectedBooks || [];
  const [expandedKey, setExpandedKey] = useState(null);
  const [lineData, setLineData] = useState({});

  const fetchLines = useCallback(async (opp) => {
    const key = `${opp.matchid}_${opp.keytype}_${opp.player_name || ''}_${opp.point || ''}`;
    if (lineData[key]) return; // already fetched
    try {
      const token = await getToken();
      const params = new URLSearchParams({
        matchid: opp.matchid,
        keytype: opp.keytype,
        bet_name: opp.bet_name,
        ...(opp.player_name && { player_name: opp.player_name }),
        ...(opp.point != null && { point: opp.point }),
      });
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/market-lines?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLineData(prev => ({ ...prev, [key]: res.data }));
    } catch (e) {
      console.error('Failed to fetch market lines', e);
    }
  }, [getToken, lineData]);

  const toggleRow = (opp) => {
    const key = `${opp.matchid}_${opp.keytype}_${opp.player_name || ''}_${opp.point || ''}`;
    if (expandedKey === key) {
      setExpandedKey(null);
    } else {
      setExpandedKey(key);
      fetchLines(opp);
    }
  };

  const handleOddsFormatChange = (format) => {
    setOddsFormat(format);
    saveOddsFormat(format);
  };

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const availableLeagues = useMemo(() =>
    [...new Set(opportunities.map(o => o.sporttitle))].sort(),
    [opportunities]
  );
  const availableBooks = useMemo(() =>
    [...new Set(opportunities.map(o => o.bookmakerid))].sort((a, b) =>
      (sportsbookNames[a] || a).localeCompare(sportsbookNames[b] || b)
    ),
    [opportunities]
  );
  const availableKeytypes = useMemo(() =>
    [...new Set(opportunities.map(o => o.keytype))].sort(),
    [opportunities]
  );

  const filteredOpportunities = useMemo(() => {
    return opportunities.filter(opp => {
      const odds = parseFloat(opp.price);
      const maxOddsNum = parseFloat(maxOdds);
      if (maxOdds && !isNaN(maxOddsNum) && odds > maxOddsNum) return false;

      if (dateLimit !== null) {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() + dateLimit);
        cutoff.setHours(23, 59, 59, 999);
        if (new Date(opp.commencetime) > cutoff) return false;
      }
      if (selectedLeagues.length > 0 && !selectedLeagues.includes(opp.sporttitle)) return false;
      if (selectedBooks.length > 0 && !selectedBooks.includes(opp.bookmakerid)) return false;
      if (showOnlyMyBooks && myBooks.length > 0 && !myBooks.includes(opp.bookmakerid)) return false;
      if (selectedKeytypes.length > 0 && !selectedKeytypes.includes(opp.keytype)) return false;
      return true;
    });
  }, [opportunities, maxOdds, selectedLeagues, selectedBooks, selectedKeytypes, dateLimit, showOnlyMyBooks, myBooks]);

  const fetchOpportunities = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/positive-ev`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpportunities(response.data);
    } catch (error) {
      console.error('Error fetching positive EV opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateKellyBet = (odds, trueProbability) => {
    const decimalOdds = odds;
    const edge = (trueProbability * decimalOdds) - 1;
    const kellyFraction = edge / (decimalOdds - 1);
    const recommendedBet = bankroll * kellyFraction * kellyMultiplier;
    return Math.max(0, recommendedBet);
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: '#0a0e1a',
        minHeight: '100vh',
        padding: '40px 60px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '3px solid #2d3748',
            borderTop: '3px solid #00d9ff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p style={{ color: '#64748b', fontSize: '16px' }}>Loading opportunities...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#0a0e1a',
      minHeight: '100vh',
      padding: '40px 60px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <h1 style={{
            fontSize: '36px',
            fontWeight: '800',
            margin: 0,
            color: '#ffffff',
            letterSpacing: '-0.5px'
          }}>
            Positive EV
          </h1>

          {/* Odds Format Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'American' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              American
            </span>
            <div
              onClick={() => handleOddsFormatChange(oddsFormat === 'American' ? 'Decimal' : 'American')}
              style={{
                width: '48px',
                height: '26px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #2d3748',
                borderRadius: '13px',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s'
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                backgroundColor: '#00d9ff',
                borderRadius: '50%',
                position: 'absolute',
                top: '2px',
                left: oddsFormat === 'American' ? '3px' : '23px',
                transition: 'left 0.2s ease'
              }} />
            </div>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: oddsFormat === 'Decimal' ? '#ffffff' : '#64748b',
              transition: 'color 0.2s'
            }}>
              Decimal
            </span>
          </div>
        </div>

        <DataBanner />

        {/* Description Section */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '10px'
        }}>
          <div
            onClick={() => setShowWhatIs(!showWhatIs)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              What is Positive EV Betting?
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showWhatIs ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showWhatIs && (
            <div style={{ marginTop: '15px' }}>
              <p style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#a0aec0',
                margin: '0 0 15px 0'
              }}>
                Positive Expected Value (+EV) betting means placing wagers where the odds offered are better
                than the true probability of winning. When you consistently bet on +EV opportunities, you can
                expect to profit over the long run, even though individual bets may lose.
              </p>
              <p style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#a0aec0',
                margin: '0 0 15px 0'
              }}>
                Think of a <span style={{ color: '#10b981', fontWeight: '600' }}>coin flip</span>. Everyone knows
                it's 50/50. Fair odds would be +100 (bet $100 to win $100). But what if someone offered
                you <span style={{ color: '#10b981', fontWeight: '600' }}>+110</span> on heads? Now you'd win $110
                on a bet that should only pay $100. That's a +EV bet.
              </p>
              <p style={{
                fontSize: '15px',
                lineHeight: '1.7',
                color: '#a0aec0',
                margin: '0 0 20px 0'
              }}>
                Obviously this is a good bet. But how much should you wager? Every dollar you have? No, because
                there's still a chance you lose. But over many bets, you will come out ahead. This is where
                bet sizing comes in. We use the <span style={{ color: '#10b981', fontWeight: '600' }}>Kelly Criterion</span>,
                a formula widely used in sports betting to determine the optimal bet size based on your edge, risk 
                tolerance, and bankroll.
              </p>

              {/* Example Box */}
              <div style={{
                backgroundColor: '#0f1419',
                border: '1px solid #2d3748',
                borderRadius: '10px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#ffffff',
                  marginBottom: '15px'
                }}>
                  Example: Coin Flip at +110 Odds
                </div>

                {/* Setup */}
                <div style={{
                  backgroundColor: '#1a1f2e',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  marginBottom: '15px'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '15px',
                    textAlign: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>TRUE PROBABILITY</div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981', fontFamily: 'monospace' }}>50%</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>(fair odds: +100)</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>ODDS OFFERED</div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#00d9ff', fontFamily: 'monospace' }}>+110</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>(implied: 47.6%)</div>
                    </div>
                  </div>
                </div>

                {/* Explanation */}
                <div style={{
                  backgroundColor: '#1a1f2e',
                  padding: '15px',
                  borderRadius: '8px',
                  border: '1px solid #2d3748',
                  marginBottom: '15px'
                }}>
                  <p style={{ fontSize: '14px', color: '#a0aec0', margin: 0, lineHeight: '1.6' }}>
                    Since the <span style={{ color: '#10b981', fontWeight: '600' }}>true probability (50%)</span> is
                    greater than the <span style={{ color: '#00d9ff', fontWeight: '600' }}>implied probability (47.6%)</span> from
                    the offered odds, this is a <span style={{ color: '#10b981', fontWeight: '600' }}>+EV bet</span>.
                  </p>
                </div>

                {/* Result Example */}
                <div style={{
                  backgroundColor: '#0f1419',
                  border: '1px solid #10b981',
                  borderRadius: '8px',
                  padding: '15px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    With $10,000 Bankroll (0.25 Kelly Multiplier)
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '30px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>EV</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981', fontFamily: 'monospace' }}>+5%</div>
                    </div>
                    <div style={{ color: '#2d3748', fontSize: '24px' }}>|</div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Recommended Bet</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981', fontFamily: 'monospace' }}>$113.64</div>
                    </div>
                    <div style={{ color: '#2d3748', fontSize: '24px' }}>|</div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>Expected Profit</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981', fontFamily: 'monospace' }}>+$5.68</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '8px',
          padding: '14px 20px',
          marginBottom: '20px'
        }}>
          <div
            onClick={() => setShowHowTo(!showHowTo)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            <h3 style={{
              margin: 0,
              fontSize: '15px',
              fontWeight: '600',
              color: '#ffffff'
            }}>
              How to Use
            </h3>
            <ChevronDown
              size={20}
              color="#a0aec0"
              style={{
                transition: 'transform 0.2s ease',
                transform: showHowTo ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </div>
          {showHowTo && (
            <div style={{
              fontSize: '15px',
              lineHeight: '1.8',
              color: '#a0aec0',
              marginTop: '15px'
            }}>
              <p style={{ margin: '0 0 10px 0' }}>
                1. Set your bankroll and Kelly fraction based on your risk tolerance
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                2. Adjust max odds and sportsbook filters to match your desired criteria
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                3. Review the opportunities' sportsbooks, EV %, and recommended bet size
              </p>
              <p style={{ margin: '0 0 10px 0' }}>
                4. Use the calculator (<Calculator size={16} color="#a0aec0" style={{ verticalAlign: 'middle' }} />) for detailed information and verification
              </p>
              <p style={{ margin: 0 }}>
                5. Place your bet at the specified sportsbook
              </p>
            </div>
          )}
        </div>

        {/* Opportunities Table */}
        <div style={{
          backgroundColor: '#1a1f2e',
          border: '1px solid #2d3748',
          borderRadius: '12px',
          padding: '20px'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '15px',
            borderBottom: '1px solid #2d3748'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                background: 'linear-gradient(135deg, rgba(0, 217, 255, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)',
                border: '1px solid rgba(0, 217, 255, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TrendingUp size={16} color="#00d9ff" />
              </div>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#ffffff'
              }}>
                Live Opportunities
              </h2>
              <div style={{
                padding: '4px 12px',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#10b981'
              }}>
                {filteredOpportunities.length} ACTIVE
              </div>
            </div>

            {/* Settings & Filters */}
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Settings size={14} color="#a0aec0" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Bankroll
                  </label>
                  <input
                    type="number"
                    step="1000"
                    value={bankroll}
                    onChange={(e) => setBankroll(Number(e.target.value))}
                    style={{
                      padding: '8px 12px',
                      width: '90px',
                      fontSize: '13px',
                      fontWeight: '600',
                      fontFamily: 'monospace',
                      backgroundColor: '#0f1419',
                      border: '1px solid #2d3748',
                      borderRadius: '6px',
                      color: '#ffffff',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                    onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Kelly Fraction
                  </label>
                  <input
                    type="number"
                    step="0.05"
                    value={kellyMultiplier}
                    onChange={(e) => setKellyMultiplier(Number(e.target.value))}
                    style={{
                      padding: '8px 12px',
                      width: '70px',
                      fontSize: '13px',
                      fontWeight: '600',
                      fontFamily: 'monospace',
                      backgroundColor: '#0f1419',
                      border: '1px solid #2d3748',
                      borderRadius: '6px',
                      color: '#ffffff',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                    onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#64748b',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Max Odds
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={maxOdds}
                    onChange={(e) => setMaxOdds(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      width: '70px',
                      fontSize: '13px',
                      fontWeight: '600',
                      fontFamily: 'monospace',
                      backgroundColor: '#0f1419',
                      border: '1px solid #2d3748',
                      borderRadius: '6px',
                      color: '#ffffff',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#00d9ff'}
                    onBlur={(e) => e.target.style.borderColor = '#2d3748'}
                  />
                </div>
              </div>
            </div>
          </div>

          <FilterBar
            availableLeagues={availableLeagues}
            availableBooks={availableBooks}
            availableKeytypes={availableKeytypes}
            selectedLeagues={selectedLeagues}
            selectedBooks={selectedBooks}
            selectedKeytypes={selectedKeytypes}
            dateLimit={dateLimit}
            setSelectedLeagues={setSelectedLeagues}
            setSelectedBooks={setSelectedBooks}
            setSelectedKeytypes={setSelectedKeytypes}
            setDateLimit={setDateLimit}
            showOnlyMyBooks={showOnlyMyBooks}
            setShowOnlyMyBooks={setShowOnlyMyBooks}
          />

          {/* Column Headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '80px 1.5fr 140px 1fr 90px 90px 50px',
            gap: '16px',
            alignItems: 'center',
            padding: '0 20px 12px 20px',
            borderBottom: '1px solid #2d3748',
            marginBottom: '12px'
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              EV %
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Event
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              Market
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              Bet
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              True Prob
            </div>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#64748b',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              textAlign: 'center'
            }}>
              Bet Size
            </div>
            <div></div>
          </div>

          {/* Opportunities List */}
          {filteredOpportunities.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px',
              color: '#64748b'
            }}>
              <BarChart3 size={48} color="#64748b" style={{ margin: '0 auto 20px' }} />
              <p style={{ fontSize: '16px', margin: 0 }}>
                {opportunities.length === 0
                  ? 'No positive EV opportunities available at this time.'
                  : 'No opportunities match your current filters.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredOpportunities.map((opp, index) => {
                const kellyBet = calculateKellyBet(parseFloat(opp.price), parseFloat(opp.true_prob));
                const rowKey = `${opp.matchid}_${opp.keytype}_${opp.player_name || ''}_${opp.point || ''}`;
                const isExpanded = expandedKey === rowKey;
                const rows = lineData[rowKey] || [];

                return (
                  <div key={index} style={{
                    backgroundColor: '#0f1419',
                    border: `1px solid ${isExpanded ? '#00d9ff' : '#2d3748'}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    transition: 'all 0.2s',
                    cursor: 'pointer',
                    boxShadow: isExpanded ? '0 4px 20px rgba(0, 217, 255, 0.15)' : 'none'
                  }}
                  onClick={() => toggleRow(opp)}
                  onMouseOver={(e) => { if (!isExpanded) { e.currentTarget.style.borderColor = '#00d9ff'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 217, 255, 0.15)'; }}}
                  onMouseOut={(e) => { if (!isExpanded) { e.currentTarget.style.borderColor = '#2d3748'; e.currentTarget.style.boxShadow = 'none'; }}}
                  ><div style={{ padding: '12px 20px' }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 1.5fr 140px 1fr 90px 90px 50px',
                      gap: '16px',
                      alignItems: 'center'
                    }}>
                      {/* EV % */}
                      <div style={{
                        fontSize: '22px',
                        fontWeight: '800',
                        fontFamily: 'monospace',
                        color: '#10b981',
                        textAlign: 'center'
                      }}>
                        +{(parseFloat(opp.ev) * 100).toFixed(1)}%
                      </div>

                      {/* Event */}
                      <div>
                        <div style={{
                          fontWeight: '600',
                          fontSize: '14px',
                          color: '#ffffff',
                          marginBottom: '4px'
                        }}>
                          {opp.home_team} vs. {opp.away_team}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#64748b'
                        }}>
                          {opp.sporttitle} - {formatGameTime(opp.commencetime)}
                        </div>
                      </div>

                      {/* Market Type */}
                      <MarketBadge keytype={opp.keytype} playerName={opp.player_name} />

                      {/* Bet */}
                      <div style={{
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #2d3748',
                        borderRadius: '8px',
                        padding: '12px'
                      }}>
                        <div style={{
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#ffffff',
                          marginBottom: '6px'
                        }}>
                          {opp.bet_name} {opp.point ? `(${opp.point})` : ''}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <img
                            src={`/sportsbook-icons/${sportsbookIcons[opp.bookmakerid] || 'placeholder.png'}`}
                            alt={sportsbookNames[opp.bookmakerid] || opp.bookmakerid}
                            title={sportsbookNames[opp.bookmakerid] || opp.bookmakerid}
                            style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '4px',
                              objectFit: 'contain'
                            }}
                          />
                          <span style={{
                            color: '#00d9ff',
                            fontFamily: 'monospace',
                            fontWeight: '600',
                            fontSize: '13px'
                          }}>
                            {formatOdds(opp.price, oddsFormat)}
                          </span>
                        </div>
                      </div>

                      {/* True Probability */}
                      <div style={{
                        fontSize: '16px',
                        color: '#a0aec0',
                        fontFamily: 'monospace',
                        textAlign: 'center'
                      }}>
                        {(parseFloat(opp.true_prob) * 100).toFixed(1)}%
                      </div>

                      {/* Bet Size */}
                      <div style={{
                        fontSize: '16px',
                        color: '#00d9ff',
                        fontWeight: '700',
                        fontFamily: 'monospace',
                        textAlign: 'center'
                      }}>
                        ${kellyBet.toFixed(0)}
                      </div>

                      {/* Calculator Button */}
                      <Link
                        to="/positive-ev-calculator"
                        state={{
                          bankroll: bankroll,
                          kellyMultiplier: kellyMultiplier,
                          odds: parseFloat(opp.price),
                          trueProbability: parseFloat(opp.true_prob)
                        }}
                        style={{ textDecoration: 'none' }}
                      >
                        <button style={{
                          width: '40px',
                          height: '40px',
                          padding: 0,
                          cursor: 'pointer',
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #2d3748',
                          borderRadius: '8px',
                          fontSize: '20px',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#00d9ff';
                          e.currentTarget.style.borderColor = '#00d9ff';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = '#1a1f2e';
                          e.currentTarget.style.borderColor = '#2d3748';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        >
                          <Calculator size={18} color="#a0aec0" />
                        </button>
                      </Link>
                    </div>
                  </div>{/* end inner padding div */}

                  {/* Expanded odds breakdown */}
                  {isExpanded && (
                    <div style={{
                      borderTop: '1px solid #2d3748',
                      padding: '16px 20px',
                      backgroundColor: '#0a0e1a',
                      overflowX: 'auto'
                    }}
                    onClick={e => e.stopPropagation()}
                    >
                      {lineData[rowKey] === undefined ? (
                        <div style={{ color: '#64748b', fontSize: '13px', textAlign: 'center', padding: '12px 0' }}>Loading...</div>
                      ) : rows.length === 0 ? (
                        <div style={{ color: '#64748b', fontSize: '13px', textAlign: 'center', padding: '12px 0' }}>No line data available.</div>
                      ) : (() => {
                        const bestFeatured = Math.max(...rows.map(r => parseFloat(r.featured_price)));
                        const bestOther = Math.max(...rows.map(r => parseFloat(r.other_price)));
                        const featuredName = rows[0]?.featured_name;
                        const otherName = rows[0]?.other_name;

                        return (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '400px' }}>
                            <thead>
                              <tr>
                                <td style={{ padding: '6px 10px', color: '#64748b', fontWeight: '600', width: '90px' }}>Side</td>
                                {rows.map(r => (
                                  <td key={r.bookmakerid} style={{ padding: '6px 8px', textAlign: 'center' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                                      <img
                                        src={`/sportsbook-icons/${sportsbookIcons[r.bookmakerid] || 'placeholder.png'}`}
                                        alt={sportsbookNames[r.bookmakerid] || r.bookmakerid}
                                        title={sportsbookNames[r.bookmakerid] || r.bookmakerid}
                                        style={{ width: '20px', height: '20px', objectFit: 'contain', borderRadius: '4px' }}
                                      />
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {/* Featured (EV) side */}
                              <tr>
                                <td style={{ padding: '8px 10px', color: '#a0aec0', fontWeight: '600', fontSize: '12px', textTransform: 'capitalize' }}>
                                  {featuredName} {opp.point != null ? `(${opp.point})` : ''}
                                </td>
                                {rows.map(r => {
                                  const isBest = parseFloat(r.featured_price) === bestFeatured;
                                  return (
                                    <td key={r.bookmakerid} style={{ padding: '8px', textAlign: 'center' }}>
                                      <span style={{
                                        fontFamily: 'monospace',
                                        fontWeight: isBest ? '700' : '400',
                                        fontSize: isBest ? '13px' : '12px',
                                        color: isBest ? '#00d9ff' : '#64748b',
                                        padding: isBest ? '3px 7px' : '3px 4px',
                                        backgroundColor: isBest ? 'rgba(0,217,255,0.1)' : 'transparent',
                                        borderRadius: '4px',
                                        border: isBest ? '1px solid rgba(0,217,255,0.3)' : 'none',
                                        display: 'inline-block'
                                      }}>
                                        {formatOdds(r.featured_price, oddsFormat)}
                                      </span>
                                    </td>
                                  );
                                })}
                              </tr>
                              {/* Other side */}
                              <tr>
                                <td style={{ padding: '8px 10px', color: '#a0aec0', fontWeight: '600', fontSize: '12px', textTransform: 'capitalize' }}>
                                  {otherName} {opp.point != null ? `(${opp.point})` : ''}
                                </td>
                                {rows.map(r => {
                                  const isBest = parseFloat(r.other_price) === bestOther;
                                  return (
                                    <td key={r.bookmakerid} style={{ padding: '8px', textAlign: 'center' }}>
                                      <span style={{
                                        fontFamily: 'monospace',
                                        fontWeight: isBest ? '700' : '400',
                                        fontSize: isBest ? '13px' : '12px',
                                        color: isBest ? '#00d9ff' : '#64748b',
                                        padding: isBest ? '3px 7px' : '3px 4px',
                                        backgroundColor: isBest ? 'rgba(0,217,255,0.1)' : 'transparent',
                                        borderRadius: '4px',
                                        border: isBest ? '1px solid rgba(0,217,255,0.3)' : 'none',
                                        display: 'inline-block'
                                      }}>
                                        {formatOdds(r.other_price, oddsFormat)}
                                      </span>
                                    </td>
                                  );
                                })}
                              </tr>
                            </tbody>
                          </table>
                        );
                      })()}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default PositiveEV;
